import React, {createContext, PropsWithChildren, useCallback, useContext, useMemo, useState} from "react";
import {DialogContent, DialogTitle, Typography} from "@mui/material";
import {grey} from "@mui/material/colors";
import {Dialog2} from "../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";

type ConfirmState = {
    openingNames: string[];
    resolve(confirmed: boolean): void;
}

const ConfirmWallTypeUpdateContext = createContext({
    // returns true if the user confirmed the cascade, false if cancelled
    confirm: async (openingNames: string[]): Promise<boolean> => true,
});

export function useConfirmWallTypeUpdate() {
    return useContext(ConfirmWallTypeUpdateContext);
}

export function ConfirmWallTypeUpdateProvider(props: PropsWithChildren<{}>) {
    const [state, setState] = useState<ConfirmState | null>(null);

    const confirm = useCallback((openingNames: string[]) => {
        return new Promise<boolean>((resolve) => {
            setState({openingNames, resolve});
        });
    }, []);

    const ctx = useMemo(() => ({confirm}), [confirm]);

    const done = (confirmed: boolean) => {
        state?.resolve(confirmed);
        setState(null);
    };

    const count = state?.openingNames.length ?? 0;

    return (
        <ConfirmWallTypeUpdateContext.Provider value={ctx}>
            {props.children}
            {state && <Dialog2 open onClose={() => done(false)} onEnterPress={() => done(true)} maxWidth="xs" fullWidth>
                <DialogTitle>Update {count} related opening{count === 1 ? "" : "s"}?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        {count === 1 ? "This opening shares" : "These openings share"} this wall type,
                        so your change will be applied to {count === 1 ? "it" : "them"} as well:
                    </Typography>
                    <div style={{
                        margin: "12px 0",
                        padding: "8px 12px",
                        backgroundColor: grey["100"],
                        borderRadius: 4,
                        maxHeight: 160,
                        overflowY: "auto",
                        fontSize: "0.875rem",
                    }}>
                        {state.openingNames.join(", ")}
                    </div>
                    <Typography variant="caption" color="text.secondary">
                        Don't want to change {count === 1 ? "it" : "these"}? Cancel and give this opening a new wall type first.
                    </Typography>
                </DialogContent>
                <SmoothActionBar
                    onCancel={() => done(false)}
                    onSubmit={() => done(true)}
                    actionName={`Update ${count} opening${count === 1 ? "" : "s"}`}
                />
            </Dialog2>}
        </ConfirmWallTypeUpdateContext.Provider>
    )
}
